import { useMutation } from '@tanstack/react-query';
import { Eye, EyeOff, Lock, Mail } from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import { authAPI } from '../api/index';
import { Button } from '../components/Button';
import { useAuthStore } from '../store/auth';

export function LoginPage() {
  const navigate = useNavigate()
  const setAuth = useAuthStore((s) => s.setAuth)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const loginMutation = useMutation({
    mutationFn: () => authAPI.login(email, password),
    onSuccess: (res) => {
      try {
        // Extract tokens from response
        const { access_token, refresh_token, user } = res.data
        setAuth({ access_token, refresh_token }, user)
        toast.success('Welcome back!')
        navigate('/dashboard')
      } catch (err) {
        toast.error('Failed to process login')
        console.error('Login processing error:', err)
      }
    },
    onError: (error: any) => {
      console.error('Login error:', error)
      toast.error(error.response?.data?.error || 'Invalid email or password')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !password) {
      toast.error('Please fill in all fields')
      return
    }
    loginMutation.mutate()
  }

  return (
    <div className="min-h-screen flex">
      {/* Left panel - Brand */}
      <div className="hidden lg:flex lg:w-1/2 bg-navy text-white flex-col justify-center items-center p-12">
        <div className="max-w-md">
          <div className="mb-8 flex justify-center">
            <img src="/image.png" alt="RTO Logo" className="h-28 rounded-lg object-cover" />
          </div>
          <div className="text-5xl font-bold mb-6 text-center">RTO Agents</div>
          <p className="text-xl text-gray-300 mb-8">
            Manage your agency's vehicle documents with ease
          </p>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-saffron rounded-full flex items-center justify-center mt-1">
                ✓
              </div>
              <p className="text-gray-200">Complete document management system</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-saffron rounded-full flex items-center justify-center mt-1">
                ✓
              </div>
              <p className="text-gray-200">Real-time expiry tracking and alerts</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-saffron rounded-full flex items-center justify-center mt-1">
                ✓
              </div>
              <p className="text-gray-200">Team collaboration and permissions</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel - Login form */}
      <div className="flex-1 flex flex-col justify-center items-center p-8 bg-white">
        <div className="max-w-md w-full">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Sign In</h1>
            <p className="text-gray-600 mt-2">Welcome back to RTO Agents</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-saffron"
                  disabled={loginMutation.isPending}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-saffron"
                  disabled={loginMutation.isPending}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <div className="text-right mt-2">
                <Link
                  to="/forgot-password"
                  className="text-sm text-saffron hover:text-orange-600 font-medium"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              isLoading={loginMutation.isPending}
              disabled={loginMutation.isPending}
            >
              Sign In
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-center text-gray-600 text-sm">
              Don&apos;t have an account?{' '}
              <Link to="/register" className="text-saffron hover:text-orange-600 font-medium">
                Register your agency
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
